/**
 * MapperHeader — top control bar for the layer-centric Mapper shell.
 *
 * Relocates the existing mode toolbar (DrawToolbar, inline) into the header and
 * adds first-class actions: Import (opens the import drawer), Find by entity,
 * and Detect infrastructure (agentic scan). The header is expandable so the
 * map keeps maximum vertical space when collapsed.
 *
 * Reuses existing surfaces only:
 *  - DrawToolbar (placement="inline") for Select/Marker/Polygon/Line + measure.
 *  - useFarmInfrastructureScan for the agentic detect (mapper-detection agent).
 *  - mapper:open-import / mapper:open-find-entity window events consumed by the
 *    drawer/find components mounted in MapperEmbed.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronUp,
  Upload,
  Search,
  Sparkles,
  Loader2,
  Map as MapIcon,
} from 'lucide-react';
import DrawToolbar from './DrawToolbar';
import { useMapStore } from '../MapContext';
import { useMapperPluginConfig, landUnitLabel } from '../mapperConfig';
import { useFarmInfrastructureScan } from '../useFarmInfrastructureScan';
import { useSuggestionMetrics } from '../api';
import { MAPPER_OPEN_IMPORT_EVENT, MAPPER_OPEN_FIND_ENTITY_EVENT } from '../constants';
import { toast } from 'sonner';

interface MapperHeaderProps {
  editingEnabled?: boolean;
  quickPresetIds?: string[];
}

export default function MapperHeader({ editingEnabled = true, quickPresetIds }: MapperHeaderProps) {
  const [expanded, setExpanded] = useState(true);
  const { activeParcel, selectedParcels } = useMapStore();
  const { data: configData } = useMapperPluginConfig();
  const config = configData?.config;
  const detectEnabled = Boolean(config?.layers?.objects?.detect_enabled);
  const landUnitSingular = landUnitLabel(config, false);

  const { runScan, isPending: scanPending } = useFarmInfrastructureScan();
  const { data: metrics } = useSuggestionMetrics({ parcel_id: activeParcel?.id });
  const reviewCount = metrics?.review ?? 0;

  const scanTargets = selectedParcels.length > 0 ? selectedParcels : activeParcel ? [activeParcel] : [];

  const handleDetect = async () => {
    if (!detectEnabled) {
      toast.info('Object detection is not enabled for this app.');
      return;
    }
    if (scanTargets.length === 0) {
      toast.info(`Select a ${landUnitSingular.toLowerCase()} or parcel first, then run detect.`);
      return;
    }
    try {
      await runScan({ parcels: scanTargets, replaceExisting: false });
      toast.success('Detection started — candidates will appear in the review tray.');
    } catch (err) {
      toast.error('Could not start detection', { description: String(err) });
    }
  };

  const openImport = () => window.dispatchEvent(new CustomEvent(MAPPER_OPEN_IMPORT_EVENT));
  const openFindEntity = () => window.dispatchEvent(new CustomEvent(MAPPER_OPEN_FIND_ENTITY_EVENT));

  return (
    <div className="z-30 flex-shrink-0 border-b border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex items-center gap-2">
          <MapIcon className="h-4 w-4 text-emerald-600" />
          <span className="text-sm font-semibold text-gray-800">Mapper</span>
          {reviewCount > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
              {reviewCount} to review
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {editingEnabled && (
            <button
              type="button"
              onClick={handleDetect}
              disabled={scanPending}
              title={detectEnabled ? 'Detect infrastructure on the selected parcel/land unit' : 'Detection not enabled for this app'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs font-semibold text-sky-700 transition-colors hover:bg-sky-100 disabled:opacity-50"
            >
              {scanPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">Detect</span>
            </button>
          )}
          {editingEnabled && (
            <button
              type="button"
              onClick={openImport}
              title="Import map data (Shapefile, GeoJSON, KML/KMZ, GeoPackage, CSV, GPX)"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              <Upload className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Import</span>
            </button>
          )}
          <button
            type="button"
            onClick={openFindEntity}
            title="Find parcels by legal entity (deeds lookup)"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Find by entity</span>
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            title={expanded ? 'Collapse tools' : 'Expand tools'}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {expanded && editingEnabled && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden border-t border-gray-100"
          >
            <div className="flex items-center gap-2 px-3 py-2">
              <DrawToolbar placement="inline" quickPresetIds={quickPresetIds} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
